import { useEffect, useRef, useState } from "react";
import {
  FolderOpen,
  Plus,
  Download,
  Trash2,
  Eye,
  X,
  File as FileIcon,
  FileImage,
  FileType,
} from "@/components/icons";
import { toast } from "sonner";
import api, { formatErr } from "../../../lib/api";
import { Card, Empty, fmtDate, formatBytes } from "./common";
import Portal from "../../../components/Portal";

export default function FilesTab({ patientId, isDoctor }) {
  const [files, setFiles] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(null); // {name, mime, data_url}
  const inputRef = useRef();

  const load = () =>
    api
      .get(`/patients/${patientId}/files`)
      .then((r) => setFiles(r.data))
      .catch((err) => {
        toast.error(formatErr(err.response?.data?.detail));
        setFiles([]);
      });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) {
      toast.error("Archivo muy grande (máx 4 MB)");
      return;
    }
    setUploading(true);
    try {
      const data_base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result.split(",")[1] || r.result);
        r.onerror = reject;
        r.readAsDataURL(f);
      });
      await api.post(`/patients/${patientId}/files`, {
        name: f.name,
        mime_type: f.type || "application/octet-stream",
        size: f.size,
        data_base64,
      });
      toast.success("Archivo subido");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const download = async (file) => {
    try {
      const r = await api.get(`/patients/${patientId}/files/${file.id}`);
      const { data_base64, mime_type, name } = r.data;
      const byteChars = atob(data_base64);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      const blob = new Blob([bytes], { type: mime_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Error al descargar");
    }
  };

  const view = async (file) => {
    try {
      const r = await api.get(`/patients/${patientId}/files/${file.id}`);
      const { data_base64, mime_type, name } = r.data;
      const data_url = `data:${mime_type};base64,${data_base64}`;
      setViewing({ name, mime: mime_type, data_url });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Error al cargar");
    }
  };

  const remove = async (file) => {
    if (!window.confirm(`¿Eliminar "${file.name}"?`)) return;
    try {
      await api.delete(`/patients/${patientId}/files/${file.id}`);
      toast.success("Archivo eliminado");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <FolderOpen className="w-4 h-4 text-black/70" strokeWidth={1.75} weight="regular" />
          <div className="text-[13px] font-semibold text-black">
            Archivos adjuntos
          </div>
        </div>
        {isDoctor && (
          <>
            <input
              ref={inputRef}
              type="file"
              hidden
              accept=".pdf,.doc,.docx,.txt,.csv,image/*"
              onChange={onPick}
              data-testid="patient-file-input"
            />
            <button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid="patient-file-upload-btn"
              title="Subir archivo"
              className="w-7 h-7 rounded-md bg-[#F2F3F5] hover:bg-black/[0.08] text-black/70 flex items-center justify-center transition disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
            </button>
          </>
        )}
      </div>

      {files === null ? (
        <Empty>Cargando…</Empty>
      ) : files.length === 0 ? (
        <Empty data-testid="patient-files-empty">
          Sin archivos adjuntos · PDF, imágenes, Word, texto (máx 4 MB)
        </Empty>
      ) : (
        <div className="overflow-x-auto" data-testid="patient-files-list">
          <table className="w-full">
            <thead>
              <tr>
                {["Fecha", "Archivo", "Tipo", "Tamaño", "Subido por", ""].map((h, i) => (
                  <th
                    key={h + i}
                    className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold text-left"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr
                  key={f.id}
                  className="border-t border-black/[0.05] text-black hover:bg-black/[0.02] transition"
                >
                  <td className="py-3 pr-3 whitespace-nowrap text-[13px] font-semibold text-black">
                    {fmtDate(f.created_at)}
                  </td>
                  <td className="py-3 px-3">
                    <div className="flex items-center gap-2">
                      <FileGlyph mime={f.mime_type} />
                      <span className="font-semibold text-[13px] text-black truncate max-w-[280px]">
                        {f.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 px-3 text-black/60 text-[11px] uppercase tracking-widest font-semibold">
                    {mimeLabel(f.mime_type)}
                  </td>
                  <td className="py-3 px-3 text-black/60 text-[13px]">{formatBytes(f.size)}</td>
                  <td className="py-3 px-3 text-black/60 text-[13px]">
                    {f.uploaded_by_name || "—"}
                  </td>
                  <td className="py-3 pl-3 text-right whitespace-nowrap">
                    {(f.mime_type?.startsWith("image/") || f.mime_type === "application/pdf") && (
                      <button
                        onClick={() => view(f)}
                        data-testid={`file-view-${f.id}`}
                        title="Ver"
                        className="w-7 h-7 rounded-md hover:bg-black/[0.05] text-black/70 hover:text-black inline-flex items-center justify-center transition"
                      >
                        <Eye className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                      </button>
                    )}
                    <button
                      onClick={() => download(f)}
                      data-testid={`file-download-${f.id}`}
                      title="Descargar"
                      className="w-7 h-7 ml-1 rounded-md hover:bg-black/[0.05] text-black/70 hover:text-black inline-flex items-center justify-center transition"
                    >
                      <Download className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                    </button>
                    {isDoctor && (
                      <button
                        onClick={() => remove(f)}
                        data-testid={`file-delete-${f.id}`}
                        title="Eliminar"
                        className="w-7 h-7 ml-1 rounded-md hover:bg-red-50 text-red-500/70 hover:text-red-600 inline-flex items-center justify-center transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewing && <FileViewer file={viewing} onClose={() => setViewing(null)} />}
    </Card>
  );
}

function FileViewer({ file, onClose }) {
  const isImage = file.mime?.startsWith("image/");
  return (
    <Portal>
      <div
        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
        data-testid="file-viewer-overlay"
      >
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl border border-black/[0.05] shadow-2xl p-4 max-w-4xl w-full max-h-[92vh] overflow-auto"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[13px] font-semibold text-black truncate">{file.name}</div>
            <div className="flex items-center gap-1">
              <a
                href={file.data_url}
                download={file.name}
                title="Descargar"
                className="w-8 h-8 rounded-lg hover:bg-black/[0.05] text-black/70 hover:text-black flex items-center justify-center transition"
              >
                <Download className="w-4 h-4" strokeWidth={1.75} weight="regular" />
              </a>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-black/[0.05] text-black/60 flex items-center justify-center transition"
                data-testid="file-viewer-close"
              >
                <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
              </button>
            </div>
          </div>
          {isImage ? (
            <img
              src={file.data_url}
              alt={file.name}
              className="w-full rounded-lg"
            />
          ) : (
            <iframe
              src={file.data_url}
              title={file.name}
              className="w-full h-[78vh] rounded-lg bg-[#F2F3F5]"
            />
          )}
        </div>
      </div>
    </Portal>
  );
}

function FileGlyph({ mime }) {
  if (mime?.startsWith("image/"))
    return <FileImage className="w-3.5 h-3.5 text-black/70 flex-shrink-0" strokeWidth={1.75} weight="regular" />;
  if (mime === "application/pdf")
    return <FileType className="w-3.5 h-3.5 text-black/70 flex-shrink-0" strokeWidth={1.75} weight="regular" />;
  return <FileIcon className="w-3.5 h-3.5 text-black/50 flex-shrink-0" strokeWidth={1.75} weight="regular" />;
}

function mimeLabel(mime) {
  if (!mime) return "—";
  if (mime.startsWith("image/")) return "Imagen";
  if (mime === "application/pdf") return "PDF";
  if (mime.includes("word")) return "Word";
  if (mime === "text/plain") return "Texto";
  if (mime === "text/csv") return "CSV";
  return mime.split("/").pop();
}
